import { isOutsideMedication } from '../../../utils/medicationOrigin';

/**
 * Transform diagnoses data for template form
 * Expects normalized data with canonical field names: {diagnosis_id, icd_code_id, icd_code, description, ...}
 * @param {Array} diagnoses - Array of normalized diagnosis objects
 * @returns {Array} Transformed diagnoses for form
 */
export function transformDiagnosesForForm(diagnoses) {
  if (!Array.isArray(diagnoses)) return [];
  
  return diagnoses.map(d => ({
    id: d.diagnosis_id,
    icd_code: d.icd_code || '',
    description: d.description || '',
    icd_code_id: d.icd_code_id,
    diagnosis_id: d.diagnosis_id,
    is_primary: d.is_primary || false,
    status: d.status || 'active',
    selected: true,
  }));
}

/**
 * Transform medications data for template form
 * Expects normalized data with canonical field names: {id, medication_name, dosage, frequency, route, status}
 * @param {Array} medications - Array of normalized medication objects
 * @returns {Array} Transformed medications for form
 */
export function transformMedicationsForForm(
  medications,
  options: { includeOutside?: boolean; includeHistory?: boolean } = {},
) {
  if (!Array.isArray(medications)) return [];
  const includeOutside = options.includeOutside === true;
  const includeHistory = options.includeHistory === true;

  const seen = new Set();
  return medications
    .filter(m => includeHistory || m.status === 'active')
    .filter(m => includeOutside || !isOutsideMedication(m))
    .filter((medication) => {
      const identity = medication.vendor_prescription_id || medication.id;
      if (!identity) return true;
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .map(m => ({
      id: m.id,
      name: m.medication_name || m.name || '',
      medication_name: m.medication_name || '',
      dosage: m.dosage || '',
      frequency: m.frequency || '',
      route: m.route || '',
      prescriber_name: m.prescriber_name || '',
      external_prescriber_name: m.external_prescriber_name || '',
      source: m.source || '',
      prescribing_responsibility: m.prescribing_responsibility || null,
      item_type: m.item_type || 'medication',
      source_reference: m.source_reference || '',
      vendor_prescription_id: m.vendor_prescription_id || '',
      is_in_house: m.is_in_house || false,
      managed_by: m.managed_by || null,
      record_provenance: m.record_provenance || null,
      rx_status: m.rx_status || null,
      insurance_history: m.insurance_history || null,
      start_date: m.start_date || null,
      end_date: m.end_date || null,
      status: m.status || '',
      clinical_status: m.clinical_status
        || (m.status === 'active' ? 'current' : m.status === 'unknown' ? 'unknown' : 'past'),
    }));
}

/**
 * Format medications as a readable string for textarea fields
 * @param {Array} medications - Array of medication objects
 * @returns {string} Formatted medication list as text
 */
export function formatMedicationsAsText(
  medications,
  options: { includeOutside?: boolean } = {},
) {
  if (!Array.isArray(medications) || medications.length === 0) return '';
  const includeOutside = options.includeOutside === true;

  const seen = new Set();
  return medications
    .filter(m => m.status === 'active')
    .filter(m => includeOutside || !isOutsideMedication(m))
    .filter((medication) => {
      const identity = medication.vendor_prescription_id || medication.id;
      if (!identity) return true;
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .map(m => {
      const parts = [m.medication_name || m.name || ''];
      if (m.dosage) parts.push(m.dosage);
      parts.push('Active');
      if (m.frequency) parts.push(m.frequency);
      if (m.route) parts.push(`(${m.route})`);
      return parts.filter(Boolean).join(' ');
    })
    .filter(line => line.trim())
    .join('\n');
}

/**
 * Transform treatment objectives for template form
 * @param {Array} objectives - Array of treatment objective objects
 * @returns {Array} Transformed objectives for form
 */
export function transformTreatmentObjectivesForForm(objectives) {
  if (!Array.isArray(objectives)) return [];

  return objectives.map(obj => ({
    id: obj.id,
    description: obj.description,
    progress_status: '',
  }));
}

/**
 * Transform safety plan data for template form
 * Converts array fields to newline-separated text for textarea display
 * @param {Object} safetyPlan - Safety plan object from chart data
 * @returns {Object} Transformed safety plan for form
 */
export function transformSafetyPlanForForm(safetyPlan) {
  if (!safetyPlan || typeof safetyPlan !== 'object') return {};

  const arrayToText = (arr) => {
    if (!Array.isArray(arr)) return '';
    return arr.filter(Boolean).join('\n');
  };

  return {
    id: safetyPlan.id,
    warning_signs: arrayToText(safetyPlan.warning_signs),
    coping_strategies: arrayToText(safetyPlan.coping_strategies),
    reasons_for_living: arrayToText(safetyPlan.reasons_for_living),
    safe_people: arrayToText(safetyPlan.safe_people),
    safe_places: arrayToText(safetyPlan.safe_places),
    professional_contacts: arrayToText(safetyPlan.professional_contacts),
    emergency_contacts: arrayToText(safetyPlan.emergency_contacts),
    means_restriction: safetyPlan.means_restriction || '',
    additional_notes: safetyPlan.additional_notes || '',
  };
}

/**
 * Transform safety plan form data for chart update API
 * Converts newline-separated text fields to arrays expected by backend
 * @param {Object} safetyPlanFormData - Safety plan data from form (textarea fields)
 * @returns {Object} Safety plan data formatted for API (List[str] fields)
 */
export function transformSafetyPlanForApi(safetyPlanFormData) {
  if (!safetyPlanFormData || typeof safetyPlanFormData !== 'object') return null;

  const textToArray = (text) => {
    if (!text || typeof text !== 'string') return null;
    const items = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
    return items.length > 0 ? items : null;
  };

  return {
    warning_signs: textToArray(safetyPlanFormData.warning_signs),
    coping_strategies: textToArray(safetyPlanFormData.coping_strategies),
    reasons_for_living: textToArray(safetyPlanFormData.reasons_for_living),
    safe_people: textToArray(safetyPlanFormData.safe_people),
    safe_places: textToArray(safetyPlanFormData.safe_places),
    professional_contacts: textToArray(safetyPlanFormData.professional_contacts),
    emergency_contacts: textToArray(safetyPlanFormData.emergency_contacts),
    means_restriction: safetyPlanFormData.means_restriction || null,
    additional_notes: safetyPlanFormData.additional_notes || null,
  };
}

/**
 * Check if a value is empty or a default placeholder
 * Used to determine if a field should be overwritten by pre-populated data
 * @param {*} val - Value to check
 * @returns {boolean} True if value is empty or default
 */
export function isEmptyOrDefault(val) {
  if (val === undefined || val === null || val === '') return true;
  if (val === 'Select...' || val === 'Not Assessed') return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
}

/**
 * Convert one client-reported intake answer into a structured medication/supplement
 * list item. The client's verbatim text is preserved as the item name — no NLP or
 * dose/frequency inference. Provenance is marked so these are never treated as
 * clinician-reconciled chart medications.
 */
function clientReportedListItem(entry) {
  if (entry === null || entry === undefined) return null;
  let verbatim = '';
  const extras = [];
  if (typeof entry === 'string') {
    verbatim = entry;
  } else if (typeof entry === 'object') {
    verbatim = String(entry.name || entry.medication_name || '');
    if (entry.dosage) extras.push(`Dosage: ${entry.dosage}`);
    if (entry.frequency) extras.push(`Frequency: ${entry.frequency}`);
    if (entry.prescriber) extras.push(`Prescriber: ${entry.prescriber}`);
    if (entry.notes) extras.push(`Notes: ${entry.notes}`);
  }
  verbatim = verbatim.trim();
  if (!verbatim) return null;
  return {
    id: null,
    name: verbatim,
    notes: extras.join('; '),
    source: 'client_report',
    isManual: true,
    client_reported: true,
  };
}

/**
 * Merge pre-populated data into existing form data without overwriting
 * Only fills in fields that are empty or have default placeholder values
 * @param {Object} existingData - Existing form data
 * @param {Object} prePopulatedData - Pre-populated data to merge
 * @returns {Object} Merged form data
 */
export function mergePrePopulatedData(existingData, prePopulatedData) {
  const merged = { ...existingData };
  
  for (const [key, value] of Object.entries(prePopulatedData)) {
    if (isEmptyOrDefault(existingData[key])) {
      merged[key] = value;
    }
  }
  
  return merged;
}

/**
 * Transform intake form responses to template form fields
 * Maps client-reported data from intake forms to corresponding note template fields
 * Each field is prefixed with "Client Report:" for clear attribution
 * @param {Object} intakeData - Intake form response data with forms array and combined_data
 * @returns {Object} Transformed data for template form fields
 */
export function transformIntakeFormDataForTemplate(intakeData) {
  if (!intakeData || typeof intakeData !== 'object') return {};

  const formData: Record<string, unknown> = {};
  const combined = intakeData.combined_data || {};

  // Track which intake form fields have been mapped to template fields
  // This allows us to capture any unmapped fields in an "additional info" section
  const mappedFields = new Set();

  // Helper to format client-reported content with attribution
  const formatClientReport = (content) => {
    if (!content) return '';
    return `Client Report:\n${content}\n\nProvider Notes:`;
  };

  // Helper to format field names for display (snake_case to Title Case)
  const formatFieldName = (fieldName) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Map presenting problem fields
  if (combined.presenting_problem) {
    let presentingContent = combined.presenting_problem;
    if (combined.presenting_problem_details) {
      presentingContent += '\n\n' + combined.presenting_problem_details;
    }
    formData.presenting_problem = formatClientReport(presentingContent);
    mappedFields.add('presenting_problem');
    mappedFields.add('presenting_problem_details');
  }

  // Map history of present illness from presenting problem details
  if (combined.presenting_problem_details) {
    formData.history_present_illness = formatClientReport(combined.presenting_problem_details);
    mappedFields.add('presenting_problem_details');
  }

  // Map psychiatric history
  if (combined.prior_mental_health) {
    formData.psychiatric_history = formatClientReport(combined.prior_mental_health);
    mappedFields.add('prior_mental_health');
  }

  // Map medical history
  if (combined.medical_issues) {
    formData.medical_history = formatClientReport(combined.medical_issues);
    mappedFields.add('medical_issues');
  }

  // Map substance use
  if (combined.substance_use) {
    formData.substance_use = formatClientReport(combined.substance_use);
    mappedFields.add('substance_use');
  }

  // Map family history (combine family mental health and general family history)
  const familyParts = [];
  if (combined.family_mental_health) {
    familyParts.push('Mental Health History: ' + combined.family_mental_health);
    mappedFields.add('family_mental_health');
  }
  if (combined.family_history) {
    familyParts.push('Family Background: ' + combined.family_history);
    mappedFields.add('family_history');
  }
  if (familyParts.length > 0) {
    formData.family_history = formatClientReport(familyParts.join('\n\n'));
  }

  // Map social history (combine multiple social fields)
  const socialParts = [];
  if (combined.social_history) {
    socialParts.push(combined.social_history);
    mappedFields.add('social_history');
  }
  if (combined.educational_vocational) {
    socialParts.push('Education/Employment: ' + combined.educational_vocational);
    mappedFields.add('educational_vocational');
  }
  if (combined.spiritual_cultural) {
    socialParts.push('Spiritual/Cultural: ' + combined.spiritual_cultural);
    mappedFields.add('spiritual_cultural');
  }
  if (socialParts.length > 0) {
    formData.social_history = formatClientReport(socialParts.join('\n\n'));
  }

  // Map developmental history if present
  if (combined.developmental_history) {
    formData.developmental_history = formatClientReport(combined.developmental_history);
    mappedFields.add('developmental_history');
  }

  // Map strengths and needs to both potential template field names
  if (combined.strengths_needs) {
    formData.snap = formatClientReport(combined.strengths_needs);
    formData.client_strengths = formatClientReport(combined.strengths_needs);
    mappedFields.add('strengths_needs');
  }

  // Map allergies (no "Provider Notes" suffix - this is factual data)
  if (combined.allergies) {
    formData.allergies = `Client Report:\n${combined.allergies}`;
    mappedFields.add('allergies');
  }

  // Map medications from intake form (client-reported) to the structured
  // current_medications list field. Each intake answer becomes one list item
  // carrying the client's verbatim text; no dose/frequency parsing is attempted.
  if (combined.medications) {
    const medsSource = Array.isArray(combined.medications)
      ? combined.medications
      : [combined.medications];
    const medItems = medsSource
      .map((med) => clientReportedListItem(med))
      .filter((item) => item !== null);
    if (medItems.length > 0) {
      formData.current_medications = medItems;
      mappedFields.add('medications');
    }
  }

  // Map supplements from intake form (client-reported) to the structured
  // current_supplements list field, same verbatim/provenance rules.
  if (Array.isArray(combined.supplements) && combined.supplements.length > 0) {
    const suppItems = combined.supplements
      .map((supp) => clientReportedListItem(supp))
      .filter((item) => item !== null);
    if (suppItems.length > 0) {
      formData.current_supplements = suppItems;
      mappedFields.add('supplements');
    }
  }

  // Map legal history if present
  if (combined.legal_history) {
    formData.legal_history = formatClientReport(combined.legal_history);
    mappedFields.add('legal_history');
  }

  // Collect any unmapped fields into additional_client_intake_info
  // This ensures we capture any new intake form fields that don't have explicit mappings
  const unmappedParts = [];
  for (const [key, value] of Object.entries(combined)) {
    if (mappedFields.has(key)) continue;
    if (value === null || value === undefined || value === '') continue;
    
    // Skip complex objects (like medications/supplements arrays) that weren't mapped
    if (typeof value === 'object') continue;
    
    unmappedParts.push(`${formatFieldName(key)}:\n${value}`);
  }
  
  if (unmappedParts.length > 0) {
    formData.additional_client_intake_info = `Client Report:\n${unmappedParts.join('\n\n')}`;
  }

  return formData;
}
